import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase, getOrganizationIdFromSession } from '../lib/supabase';
import {
  Menu,
  X,
  LayoutDashboard,
  ShoppingBag,
  Package,
  Users,
  DollarSign,
  Warehouse,
  Boxes,
  History,
  Wallet,
  UserCircle,
  CircleDollarSign,
  BarChart3,
  Receipt,
  ShieldAlert,
  UserCog,
  HandCoins,
  ClipboardCheck,
  ClipboardList,
  Settings,
  LogOut,
  Building2,
  ChevronDown,
} from 'lucide-react';
import { CurrencySelector } from './CurrencySelector';
import { LanguageSelector } from './LanguageSelector';
import { SuperAdminBanner } from './SuperAdminBanner';
import { ThemeToggle } from './ThemeToggle';
import { useThemeStore } from '../stores/themeStore';
import { getCurrentUser, clearUser, hasPermission } from '../features/auth/auth';

/** Clave de localStorage para el colapso de grupos (ADM-002). */
export const NAV_GROUPS_STORAGE_KEY = 'mibodega.admin.nav.groups';

type NavGroupId = 'inicio' | 'operacion' | 'inventario' | 'compras' | 'personas' | 'control';

type NavItem = {
  to: string;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  labelKey: string;
  requirePermission?: string;
  localOnly?: boolean;
  group: NavGroupId;
};

const GROUP_ORDER: NavGroupId[] = [
  'inicio',
  'operacion',
  'inventario',
  'compras',
  'personas',
  'control',
];

const GROUP_LABEL_KEY: Record<NavGroupId, string> = {
  inicio: 'nav.group.inicio',
  operacion: 'nav.group.operacion',
  inventario: 'nav.group.inventario',
  compras: 'nav.group.compras',
  personas: 'nav.group.personas',
  control: 'nav.group.control',
};

/**
 * 18 entradas del admin, agrupadas (ADM-002). Nada se borra: Comisiones y
 * Pagos a proveedor siguen visibles hasta ADM-006 / ADM-004. Ajustes entra
 * en Control (antes no tenía entrada de sidebar).
 */
const NAV: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', group: 'inicio' },
  { to: '/sales', icon: ShoppingBag, labelKey: 'nav.sales', group: 'operacion' },
  {
    to: '/cash-sessions',
    icon: CircleDollarSign,
    labelKey: 'nav.cashSessions',
    group: 'operacion',
  },
  {
    to: '/credits',
    icon: HandCoins,
    labelKey: 'nav.credits',
    requirePermission: 'CREDITOS_VER',
    group: 'operacion',
  },
  { to: '/products', icon: Package, labelKey: 'nav.products', group: 'inventario' },
  { to: '/stock', icon: Boxes, labelKey: 'nav.stock', group: 'inventario' },
  { to: '/warehouses', icon: Warehouse, labelKey: 'nav.warehouses', group: 'inventario' },
  { to: '/movements', icon: History, labelKey: 'nav.movements', group: 'inventario' },
  {
    to: '/inventory/close',
    icon: ClipboardCheck,
    labelKey: 'nav.inventoryClose',
    group: 'inventario',
  },
  { to: '/purchases', icon: ClipboardList, labelKey: 'nav.purchases', group: 'compras' },
  { to: '/suppliers', icon: Users, labelKey: 'nav.suppliers', group: 'compras' },
  { to: '/expenses', icon: Receipt, labelKey: 'nav.expenses', group: 'compras' },
  {
    to: '/supplier-payments',
    icon: Wallet,
    labelKey: 'nav.supplierPayments',
    requirePermission: 'PROVEEDORES_VER',
    group: 'compras',
  },
  { to: '/customers', icon: UserCircle, labelKey: 'nav.customers', group: 'personas' },
  { to: '/team', icon: UserCog, labelKey: 'nav.team', group: 'personas' },
  { to: '/exchange-rates', icon: DollarSign, labelKey: 'nav.exchangeRates', group: 'control' },
  { to: '/reports', icon: BarChart3, labelKey: 'nav.reports', group: 'control' },
  { to: '/audit', icon: ShieldAlert, labelKey: 'nav.audit', group: 'control' },
  { to: '/settings', icon: Settings, labelKey: 'nav.settings', group: 'control' },
];

function isItemActive(pathname: string, to: string): boolean {
  if (to === '/dashboard') return pathname === '/dashboard';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function groupForPath(pathname: string): NavGroupId {
  const match = NAV.find((item) => isItemActive(pathname, item.to));
  return match?.group ?? 'inicio';
}

function readStoredExpanded(): Partial<Record<NavGroupId, boolean>> | null {
  try {
    const raw = localStorage.getItem(NAV_GROUPS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Record<NavGroupId, boolean>>;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function defaultExpanded(pathname: string): Record<NavGroupId, boolean> {
  const active = groupForPath(pathname);
  return Object.fromEntries(GROUP_ORDER.map((id) => [id, id === active])) as Record<
    NavGroupId,
    boolean
  >;
}

function initialExpanded(pathname: string): Record<NavGroupId, boolean> {
  const base = defaultExpanded(pathname);
  const stored = readStoredExpanded();
  if (!stored) return base;
  for (const id of GROUP_ORDER) {
    if (typeof stored[id] === 'boolean') base[id] = stored[id] as boolean;
  }
  return base;
}

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const [checking, setChecking] = useState(true);
  // El sidebar es permanente desde `lg`; abajo de eso vive como drawer.
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState<Record<NavGroupId, boolean>>(() =>
    initialExpanded(location.pathname),
  );
  const [userInfo, setUserInfo] = useState<{
    name: string;
    email: string;
    orgName: string;
  }>({ name: '', email: '', orgName: '' });

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      clearUser();
      navigate('/login', { replace: true });
    }
  };

  const visibleNav = useMemo(
    () => NAV.filter((item) => !item.requirePermission || hasPermission(item.requirePermission)),
    [],
  );

  const grouped = useMemo(() => {
    return GROUP_ORDER.map((id) => ({
      id,
      labelKey: GROUP_LABEL_KEY[id],
      items: visibleNav.filter((item) => item.group === id),
    })).filter((g) => g.items.length > 0);
  }, [visibleNav]);

  const toggleGroup = (id: NavGroupId) => {
    setExpanded((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage puede fallar en modo privado; el colapso sigue en memoria.
      }
      return next;
    });
  };

  // Navegar cierra el drawer: si no, el usuario aterriza en la página nueva
  // con el panel tapándola.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    // Bloquea el scroll del fondo mientras el drawer está abierto.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [navOpen]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/login', { replace: true });
      } else {
        setChecking(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate('/login', { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Carga nombre del usuario + nombre de la organización para mostrar en el header.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const u = getCurrentUser();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const sessionName =
        typeof session?.user.user_metadata?.nombre === 'string'
          ? session.user.user_metadata.nombre
          : '';
      const sessionEmail = session?.user.email ?? '';
      if (cancelled) return;
      const orgClaim = await getOrganizationIdFromSession();
      if (cancelled) return;
      let orgName = '';
      if (orgClaim?.organizationId) {
        const { data } = await supabase
          .from('organizations')
          .select('nombre')
          .eq('id', orgClaim.organizationId)
          .maybeSingle();
        if (!cancelled) orgName = (data?.nombre as string) ?? '';
      }
      if (cancelled) return;
      setUserInfo({
        name: u?.nombreCompleto || sessionName || u?.email || sessionEmail,
        email: u?.email || sessionEmail,
        orgName,
      });
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-neutral-50">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {navOpen && (
        <div
          className="bg-backdrop fixed inset-0 z-30 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        id="sidebar-nav"
        className={`bg-neutral-0 fixed inset-y-0 left-0 z-40 w-64 overflow-y-auto border-r border-neutral-200 transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between gap-2 px-6">
          <h1 className="text-primary text-xl font-bold tracking-tight">MiBodega Admin</h1>
          <button
            type="button"
            onClick={() => {
              setNavOpen(false);
              menuButtonRef.current?.focus();
            }}
            className="-mr-3 flex h-11 w-11 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-100 lg:hidden"
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Navegación principal" className="flex flex-col gap-3 px-3 py-2">
          {grouped.map((group) => {
            const open = expanded[group.id] ?? false;
            const panelId = `nav-group-${group.id}`;
            return (
              <div key={group.id} className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={open}
                  aria-controls={panelId}
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold tracking-wide text-neutral-500 uppercase hover:bg-neutral-100 hover:text-neutral-700"
                >
                  <span>{t(group.labelKey)}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
                    aria-hidden="true"
                  />
                </button>
                {open && (
                  <div
                    id={panelId}
                    role="group"
                    aria-label={t(group.labelKey)}
                    className="flex flex-col gap-0.5"
                  >
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = isItemActive(location.pathname, item.to);
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          aria-current={active ? 'page' : undefined}
                          className={`flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                            active
                              ? 'bg-primary-50 text-primary font-semibold'
                              : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
                          }`}
                        >
                          <Icon
                            className={`h-5 w-5 ${active ? 'text-primary' : 'text-neutral-500'}`}
                            aria-hidden="true"
                          />
                          {t(item.labelKey)}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* `min-w-0`: sin esto el hijo flex no puede encogerse por debajo de su
          contenido y una tabla ancha empuja scroll horizontal a toda la página. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <SuperAdminBanner />
        <header className="bg-neutral-0 flex h-16 items-center gap-3 border-b border-neutral-200 px-4 sm:px-6">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setNavOpen((open) => !open)}
            aria-label="Abrir menú de navegación"
            aria-expanded={navOpen}
            aria-controls="sidebar-nav"
            className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-neutral-700 hover:bg-neutral-100 lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          {userInfo.orgName && (
            <div
              className="hidden min-w-0 items-center gap-1.5 truncate text-xs text-neutral-600 md:flex"
              data-testid="header-org-name"
            >
              <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate font-medium">{userInfo.orgName}</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {userInfo.name && (
              <div
                className="hidden max-w-[200px] truncate text-xs text-neutral-700 lg:block"
                title={userInfo.email || userInfo.name}
                data-testid="header-user-name"
              >
                <span className="font-medium">{userInfo.name}</span>
              </div>
            )}
            <button
              type="button"
              onClick={handleLogout}
              aria-label={t('nav.logout')}
              title={t('nav.logout')}
              data-testid="header-logout"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
            <LanguageSelector />
            <CurrencySelector />
            <ThemeToggle theme={theme} onChange={setTheme} />
          </div>
        </header>
        <main className="flex-1 overflow-auto bg-neutral-50 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
